import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { ToastrService } from 'ngx-toastr'
import { selectAllCountries } from '../../../store/country/country.selectors'
import { debounceTime, distinctUntilChanged, Observable, Subject } from 'rxjs'
import { loadCountries } from '../../../store/country/country.actions'
import { CommonModule, NgFor, NgIf } from '@angular/common'
import {
  incomingSubscription,
  SubscriptionAnalytics,
  SubscriptionListResponse,
  SubscriptionsService,
  SubscriptionStatus
} from '../../../services/subscriptions'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { Router, RouterModule } from '@angular/router'
import { eps } from '../../../services/url.service'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { User } from '../../../store/user/user.state'
import { UtilsService } from '../../../services/utils.service'

@Component({
  selector: 'app-subscribers',
  imports: [
    NgFor,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgbTooltipModule,
    CapitalizePipe,
    PaginationComponent,
    RouterModule
  ],
  templateUrl: './subscribers.component.html',
  styleUrl: './subscribers.component.scss'
})
export class SubscribersComponent implements OnInit {
  store = inject(Store)
  router = inject(Router)
  eps = eps
  modalService = inject(NgbModal)
  fb = inject(FormBuilder)
  form!: FormGroup
  @ViewChild('addPlanModal') addPlanModal!: TemplateRef<any>
  @ViewChild('deleteModal') deleteModal!: TemplateRef<any>
  toastr = inject(ToastrService)
  SubscriptionsService = inject(SubscriptionsService)
  subscriptions: incomingSubscription[] = []
  analytics: SubscriptionAnalytics = {
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    inactiveSubscriptions: 0,
    trialSubscriptions: 0
  }
  operation = ''
  pageLimit = 10
  selectedSubscription: incomingSubscription | null = null
  selectedPlanType: 'monthly' | 'yearly' | null = null
  selectedSubscriptionStatus: SubscriptionStatus | null = null
  subscriptionStatus = SubscriptionStatus
  subscriptionStatusValues = Object.values(SubscriptionStatus)
  currentPage = 1
  limit = 10
  search = ''
  totalItems = 0
  totalPages = 0
  pages: number[] = []
  countries$ = this.store.select(selectAllCountries)
  private searchSubject = new Subject<string>()
  subscribers$!: Observable<User[]>
  Math = Math
  totalCount = 0

  takenPositions: { monthly: number[]; yearly: number[] } = { monthly: [], yearly: [] }
  availablePositions: number[] = []
  // Available Features Management
  allPossibleFeatures = [
    { id: 'clock_in_out', name: 'Clock In/Out' },
    { id: 'permission_sets', name: 'Permission Sets' },
    { id: 'holidays', name: 'Holidays' },
    { id: 'leaves', name: 'Leaves' },
    { id: 'payrolls', name: 'Payrolls' },
    { id: 'salary', name: 'Salary' },
    { id: 'attendance_policy', name: 'Attendance Policy' },
    { id: 'payslips', name: 'Payslips' },
    { id: 'requests', name: 'Requests' }
  ]

  constructor(private utilsService: UtilsService) {}
  ngOnInit() {
    this.store.dispatch(loadCountries())
    this.initializeForm()
    this.getSubscriptions()
    this.getAnalytics()

    this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      //this.onFilterChange(true)
      this.currentPage = 1 // Reset to page 1 on search
      this.getSubscriptions()
    })
  }

  initializeForm() {
    this.form = this.fb.group({
      id: [null],
      name: ['', Validators.required],
      type: ['yearly', Validators.required],
      position: [null, Validators.required],
      minimumLicences: [0, Validators.required],
      price: [0, [Validators.required, Validators.min(0)]],
      currencyId: [5, Validators.required],
      discountType: ['percentage', Validators.required],
      discountValue: [0, Validators.required],
      availableFeatures: this.fb.array(this.allPossibleFeatures.map(() => this.fb.control(false))),
      trialDays: [0, Validators.required],
      isRecommended: [false, Validators.required],
      isActive: [true, Validators.required],
      description: ['']
    })
    // Listen for changes in the plan type dropdown
    //this.form.get('type')?.valueChanges.subscribe(() => {
    //  this.updateAvailablePositions(this.selectedSubscription)
    //  // Reset position when type changes to avoid invalid position selection
    //  this.form.get('position')?.setValue(null)
    //})
  }

  get featuresFormArray(): FormArray {
    return this.form.get('availableFeatures') as FormArray
  }

  getAnalytics() {
    this.SubscriptionsService.getAnalytics().subscribe({
      next: (response: SubscriptionAnalytics) => {
        this.analytics = response
      },
      error: (error: { message: string }) => {
        this.toastr.error('Error fetching analytics: ' + error.message)
      }
    })
  }

  getSubscriptions() {
    const filters = {
      page: this.currentPage,
      // limit: this.limit,
      limit: this.pageLimit,
      type: this.selectedPlanType,
      status: this.selectedSubscriptionStatus,
      search: this.search.trim()
    }

    this.SubscriptionsService.list(filters).subscribe({
      next: (response: SubscriptionListResponse) => {
        if (response.data) {
          this.subscriptions = response.data
          this.totalItems = response.total
          this.totalCount = response.total
        }
        this.calculatePagination()
      },
      error: (error: { message: string }) => {
        this.toastr.error('Error fetching subscriptions: ' + error.message)
      }
    })
  }

  openModal(modal: unknown) {
    //this.operation = 'Add'
    //this.selectedPlan = null
    //this.initializeForm()
    //this.modalService.open(modal, {
    //  scrollable: false,
    //  centered: true,
    //  size: 'lg'
    //})
  }

  openEditModal(plan: incomingSubscription, modal: TemplateRef<any>) {
    //this.operation = 'Edit'
    //this.selectedPlan = plan
    //this.initializeForm()
    //this.form.patchValue(plan)
    //const featureValues = this.allPossibleFeatures.map(
    //  feature => plan.availableFeatures?.includes(feature.id) ?? false
    //)
    //// Manually set checkbox values for FormArray
    ////this.featuresFormArray.controls.forEach((control, i) => {
    ////  const featureId = this.allPossibleFeatures[i].id
    ////  if (plan.availableFeatures?.includes(featureId)) {
    ////    control.setValue(true)
    ////  }
    ////})
    //this.featuresFormArray.setValue(featureValues)
    //this.modalService.open(modal, {
    //  scrollable: false,
    //  centered: true,
    //  size: 'lg'
    //})
  }

  openDeleteModal(sub: incomingSubscription, modal: TemplateRef<any>) {
    //this.selectedPlan = plan
    //this.modalService.open(modal, { centered: true })
  }

  closeModal() {
    //this.operation = ''
    //this.selectedPlan = null
    //this.modalService.dismissAll()
  }

  routeToLicenceHistory(id: string) {
    this.router.navigate([eps.superAdmin.licenceHistoryPath, id])
  }

  calculatePagination() {
    // this.totalPages = Math.ceil(this.totalItems / this.limit)
    this.totalPages = Math.ceil(this.totalItems / this.pageLimit)
    // Create an array of page numbers [1, 2, 3...] for the template
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1)
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage
    this.getSubscriptions()
  }

  onFilterChange(resetPage = false) {
    if (resetPage) this.currentPage = 1
    this.getSubscriptions()
  }

  onSearchChange(searchValue: string) {
    this.searchSubject.next(searchValue)
  }

  onPageLimitChange() {
    this.currentPage = 1
    this.getSubscriptions()
  }

  isBusinessOwner(): boolean {
    return this.utilsService.isBusinessOwner()
  }

  isAdmin(): boolean {
    return this.utilsService.isAdmin()
  }
}
